import { create } from "zustand";
import { V2_ENDPOINT } from "../consts";
import { authenticatedFetch } from "../lib/authenticatedFetch";
import { fetchAllPages } from "../lib/fetchAllPages";
import { trackConnectorCreated, trackConnectorDeleted } from "../lib/analytics";
import { createLoader } from "@/lib/stores/createLoader";
import type { PaginatedData } from "../types/pagination";

// Connector type definitions based on API response
export interface ConnectorDataSchema {
  type: "object";
  properties: Record<string, any>;
  required?: string[];
  additionalProperties?: boolean;
}

export interface ConnectorProviderConfig {
  appSlug: string;
  authPropName: string;
  authType: "oauth" | "keys";
}

export interface ConnectorType {
  type: string; // e.g., "slack", "discord", "pipedream:gmail", etc.
  name: string;
  description: string;
  dataSchema: ConnectorDataSchema;
  // Optional fields for extended connector info
  logoUrl?: string;
  informationUrl?: string;
  categories?: string[]; // Deprecated: use tags instead
  tags?: string[]; // Tags for filtering and categorization
  provider?: string; // e.g., "pipedream" for Pipedream-backed connectors
  providerConfig?: ConnectorProviderConfig;
  optional?: boolean; // If true, connector can be omitted when required by an action
  defaultOnly?: boolean; // If true, platform-managed — users cannot create their own instances
  setupGuide?: string; // Markdown setup instructions
}

export interface Connector {
  id: string;
  type: string;
  name: string;
  description: string;
  data: Record<string, any>;
  maskedData?: Record<string, any>;
  createdAt: string;
  updatedAt?: string;
}

interface ConnectorTypesApiResponse {
  code: number;
  message: string;
  data: ConnectorType[];
  requestId: string;
}

interface ConnectorsApiResponse {
  code: number;
  message: string;
  data: PaginatedData<Connector>;
  requestId: string;
}

interface ConnectorApiResponse {
  code: number;
  message: string;
  data: Connector;
  requestId: string;
}

export interface CreateConnectorRequest {
  type: string;
  name: string;
  description: string;
  data: Record<string, any>;
}

export interface UpdateConnectorRequest {
  name?: string;
  description?: string;
  data?: Record<string, any>;
}

interface ConnectorsStore {
  connectorTypes: ConnectorType[];
  connectors: Connector[];
  loading: boolean;
  error: Error | null;
  lastLoadedAt: number | null;
  // Tab state for guide integration
  activeTab: "available" | "configured" | null;
  setActiveTab: (tab: "available" | "configured") => void;
  loadConnectorTypes: () => Promise<void>;
  loadConnectors: () => Promise<void>;
  getConnectorById: (id: string) => Connector | undefined;
  createConnector: (request: CreateConnectorRequest) => Promise<Connector>;
  updateConnector: (id: string, request: UpdateConnectorRequest) => Promise<Connector>;
  deleteConnector: (id: string) => Promise<void>;
  reset: () => void;
}

// Helper function to fetch connector types
const fetchConnectorTypes = async (): Promise<ConnectorType[]> => {
  const response = await authenticatedFetch<ConnectorTypesApiResponse>(`${V2_ENDPOINT}/v1/connector-types`, {
    skipAuth: true,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch connector types: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }

  return data.data;
};

// Helper function to fetch connectors (pages through all results)
const fetchConnectors = async (): Promise<Connector[]> => {
  return fetchAllPages(async (limit, offset) => {
    const params = new URLSearchParams();
    params.append("limit", String(limit));
    params.append("offset", String(offset));

    const response = await authenticatedFetch<ConnectorsApiResponse>(
      `${V2_ENDPOINT}/v1/connectors?${params.toString()}`,
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch connectors: ${response.statusText}`);
    }

    const data = await response.json();

    if (data.code !== 0) {
      throw new Error(`API error: ${data.message}`);
    }

    return { items: data.data.items, hasMore: data.data.hasMore };
  });
};

// Loaders handle in-flight dedup, staleness, and error transience.
// See lib/stores/createLoader.ts.
const connectorTypesLoader = createLoader({
  fetch: fetchConnectorTypes,
  staleMs: 5 * 60 * 1000, // connector types rarely change between deploys
});
const connectorsLoader = createLoader({
  fetch: fetchConnectors,
});

// Dedup concurrent createConnector calls for the same (type, name) pair.
// Without this, any two ConnectorSelector instances mounting in the same tick
// both fire identical auto-create POSTs, and the backend's TOCTOU uniqueness
// check can let both through, producing duplicate rows.
const inFlightCreates = new Map<string, Promise<Connector>>();

export const useConnectorsStore = create<ConnectorsStore>((set, get) => ({
  connectorTypes: [],
  connectors: [],
  loading: false,
  error: null,
  lastLoadedAt: null,
  activeTab: null,
  setActiveTab: (tab: "available" | "configured") => set({ activeTab: tab }),

  loadConnectorTypes: async () => {
    // Only show loading state if we don't have cached data. When the
    // loader's staleMs cache is warm, load() resolves synchronously and
    // a hard `loading: true` set would cause a one-tick flicker that
    // observers can pick up (e.g. a spinner blinking between navigations).
    const { connectorTypes: current } = get();
    set({ loading: current.length === 0, error: null });
    try {
      const connectorTypes = await connectorTypesLoader.load();
      set({
        connectorTypes: connectorTypes || [],
        loading: false,
      });
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to load connector types");
      console.error("Failed to load connector types", error);
      set({ error, loading: false });
    }
  },

  loadConnectors: async () => {
    const { connectors: currentConnectors } = get();
    // Only show loading state if we don't have cached data (SWR)
    set({ loading: currentConnectors.length === 0, error: null });
    try {
      const connectors = await connectorsLoader.load();
      set({
        connectors: connectors || [],
        lastLoadedAt: Date.now(),
        loading: false,
      });
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to load connectors");
      console.error("Failed to load connectors", error);
      set({ error, loading: false });
    }
  },

  getConnectorById: (id: string): Connector | undefined => {
    const { connectors } = get();
    return connectors.find(c => c.id === id);
  },

  createConnector: async (request: CreateConnectorRequest): Promise<Connector> => {
    const key = `${request.type}:${request.name}`;
    const existing = inFlightCreates.get(key);
    if (existing) return existing;

    const promise = (async (): Promise<Connector> => {
      try {
        const response = await authenticatedFetch<ConnectorApiResponse>(`${V2_ENDPOINT}/v1/connectors`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: response.statusText }));
          throw new Error(errorData.message || `Failed to create connector: ${response.statusText}`);
        }

        const data = await response.json();

        if (data.code !== 0) {
          throw new Error(`API error: ${data.message}`);
        }

        const newConnector = data.data;

        console.log(`Connector "${newConnector.name}" created successfully with ID: ${newConnector.id}`);

        // Track connector creation
        trackConnectorCreated({
          id: newConnector.id,
          type: newConnector.type,
        });

        connectorsLoader.reset();

        // Reload connectors so the list includes maskedData from the API
        // (the create response doesn't include maskedData).
        await get().loadConnectors();

        return newConnector;
      } catch (e) {
        const error = e instanceof Error ? e : new Error("Failed to create connector");
        console.error("Failed to create connector", error);
        throw error;
      } finally {
        inFlightCreates.delete(key);
      }
    })();

    inFlightCreates.set(key, promise);
    return promise;
  },

  updateConnector: async (id: string, request: UpdateConnectorRequest): Promise<Connector> => {
    try {
      const response = await authenticatedFetch<ConnectorApiResponse>(`${V2_ENDPOINT}/v1/connectors/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Failed to update connector: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.code !== 0) {
        throw new Error(`API error: ${data.message}`);
      }

      const updatedConnector = data.data;

      connectorsLoader.reset();

      // Update the connector in the local state
      set(state => ({
        connectors: state.connectors.map(c => (c.id === id ? updatedConnector : c)),
      }));

      console.log(`Connector ${id} updated successfully`);
      return updatedConnector;
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to update connector");
      console.error("Failed to update connector", error);
      throw error;
    }
  },

  deleteConnector: async (id: string): Promise<void> => {
    // Get connector type before deletion for tracking
    const connector = get().connectors.find(c => c.id === id);
    const connectorType = connector?.type || "unknown";

    try {
      const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/connectors/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Failed to delete connector: ${response.statusText}`);
      }

      connectorsLoader.reset();

      // Remove the connector from local state
      set(state => ({
        connectors: state.connectors.filter(c => c.id !== id),
      }));

      console.log(`Connector ${id} deleted successfully`);

      // Track connector deletion
      trackConnectorDeleted(id, connectorType);
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to delete connector");
      console.error("Failed to delete connector", error);
      throw error;
    }
  },

  reset: () => {
    inFlightCreates.clear();
    set({
      connectorTypes: [],
      connectors: [],
      loading: false,
      error: null,
      lastLoadedAt: null,
      activeTab: null,
    });
  },
}));
